// teamDataProcessor.js - Centralized team data processing and constants
import { calculateTeamScores, determineFocusTrede, validateResponseData } from './dashboardCalculations';

/**
 * Constants for team data processing
 */
export const TREDES = ['vertrouwen', 'conflicten', 'betrokkenheid', 'verantwoording', 'resultaten'];

export const TREDE_NAMES = {
  vertrouwen: 'Vertrouwen',
  conflicten: 'Conflicten', 
  betrokkenheid: 'Commitment', // Veranderd van Betrokkenheid
  verantwoording: 'Verantwoording',
  resultaten: 'Gezamenlijk belang' // veranderd nav gebruikersfeedback
};

/**
 * Process team response data into complete team data structure
 * @param {Array} responses - Array of team response objects
 * @returns {Object} Complete team data with scores, names, focus, etc.
 */
export const processTeamData = (responses) => {
  // Validate input data
  const validation = validateResponseData(responses);
  
  if (!validation.isValid || validation.isEmpty) {
    return {
      scores: {},
      tredeNames: TREDE_NAMES,
      tredes: TREDES,
      focusTrede: null,
      validation,
      hasData: false
    };
  }

  // Calculate team scores using helper with correct parameters
  const scores = calculateTeamScores(responses, TREDES);
  
  // Determine focus trede (lowest scoring below threshold)
  const focusTrede = determineFocusTrede(scores, TREDES, 3.5);
  
  // Return complete processed data structure
  return {
    scores,
    tredeNames: TREDE_NAMES,
    tredes: TREDES,
    focusTrede,
    validation,
    hasData: true,
    teamSize: responses.length
  };
};

/**
 * Get team overview data for dashboard overview mode
 * @param {Array} teams - Array of team objects
 * @param {Object} responseCounts - Object mapping teamId to response count
 * @returns {Array} Processed team overview data
 */
export const processTeamOverview = (teams, responseCounts) => {
  return teams.map(team => ({
    ...team,
    responseCount: responseCounts[team.id] || 0,
    hasResponses: (responseCounts[team.id] || 0) > 0,
    measurementPeriod: team.measurementPeriod || 'Onbekend'
  }));
};

/**
 * Create team query filters for fetching teams
 * @param {Object} currentUser - Current authenticated user
 * @param {Object} userProfile - User profile with organization info
 * @param {boolean} latestOnly - Whether to filter for latest measurements only
 * @returns {Array} Array of where clauses for Firestore query
 */
export const createTeamQueryFilters = (currentUser, userProfile, latestOnly = true) => {
  const filters = [
    ['createdByEmail', '==', currentUser.email],
    ['organizationId', '==', userProfile.organizationId]
  ];
  
  if (latestOnly) {
    filters.push(['isLatestMeasurement', '==', true]);
  }
  
  return filters;
};

/**
 * Prepare data for IndividualScores component
 * @param {Array} responses - Team response data
 * @param {Object} teamData - Processed team data from processTeamData
 * @returns {Object} Props object for IndividualScores component
 */
export const prepareIndividualScoresProps = (responses, teamData) => {
  return {
    responses,
    teamScores: teamData.scores,
    tredeNames: teamData.tredeNames,
    tredes: teamData.tredes,
    focusTrede: teamData.focusTrede,
    hasData: teamData.hasData
  };
};

/**
 * Prepare data for SpiderChart component  
 * @param {Object} teamData - Processed team data from processTeamData
 * @returns {Object} Props object for SpiderChart component
 */
export const prepareSpiderChartProps = (teamData) => {
  return {
    teamScores: teamData.scores,  // ← FIX: SpiderChart expects 'teamScores', not 'scores'
    tredeNames: teamData.tredeNames,
    hasData: teamData.hasData
  };
};

/**
 * Get formatted team statistics for display
 * @param {Array} responses - Team responses
 * @param {Array} invitations - Team invitations
 * @returns {Object} Formatted statistics
 */
export const getTeamStatistics = (responses, invitations) => {
  const responseCount = responses.length;
  const invitationCount = invitations.length;
  const completionRate = invitationCount > 0 ? Math.round((responseCount / invitationCount) * 100) : 0;
  
  return {
    responseCount,
    invitationCount, 
    completionRate,
    hasResponses: responseCount > 0,
    isComplete: responseCount === invitationCount && invitationCount > 0
  };
};